import { api } from './api'
import { API_BASE_URL } from './config'

export type SupportSender = 'client' | 'agent' | 'system'
export type SupportAttachmentKind = 'image' | 'document' | 'audio' | 'voice' | 'video'

export interface SupportAttachment {
  id: string
  kind: SupportAttachmentKind
  mime: string
  filename: string
  size_bytes: number
  duration_sec?: number | null
  width?: number | null
  height?: number | null
  url: string
}

export interface SupportMessage {
  id: string
  sender: SupportSender
  content: string
  created_at: string
  attachments: SupportAttachment[]
}

export type SupportRating = 'positive' | 'negative'

export interface SupportConversation {
  id: string
  status: 'open' | 'resolved'
  last_message_at: string | null
  created_at: string
  session_token?: string
  rating?: SupportRating | null
}

export interface MessagesPayload {
  conversation: SupportConversation
  messages: SupportMessage[]
}

const STORAGE_KEY = 'callizy_support_session_v1'

interface PersistedSession {
  conv_id: string
  token: string
  /**
   * Dernier instant où le visiteur a réellement REGARDÉ la conversation.
   * Sert à décider, au chargement d'une page, si une réponse arrivée entre
   * deux visites doit allumer la pastille.
   */
  seen_at?: string
}

export const supportSession = {
  get(): PersistedSession | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? (JSON.parse(raw) as PersistedSession) : null
    } catch {
      return null
    }
  },
  set(session: PersistedSession) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  },
  /** Marque la conversation comme lue à l'instant présent. */
  markSeen() {
    const s = supportSession.get()
    if (s) supportSession.set({ ...s, seen_at: new Date().toISOString() })
  },
  clear() {
    localStorage.removeItem(STORAGE_KEY)
  },
}

/** Conversation telle qu'affichée dans l'historique (sans jeton de reprise). */
export interface SupportConversationSummary {
  id: string
  status: 'open' | 'resolved'
  last_message_at: string | null
  created_at: string
  rating?: 'positive' | 'negative' | null
  /** Début du dernier message, pour reconnaître la conversation. */
  preview: string
}

/**
 * Conversations passées du client connecté, résolues comprises.
 *
 * La reprise ordinaire ne cible que la conversation ouverte : sans cette
 * liste, tout ce qui avait été dit devenait inaccessible dès que l'équipe
 * marquait la conversation résolue.
 */
export async function fetchSupportConversations(): Promise<SupportConversationSummary[]> {
  const res = await api.get<{ conversations: SupportConversationSummary[] }>(
    '/api/support/conversations/',
  )
  return res.data.conversations ?? []
}

/**
 * Rouvre une conversation résolue et renvoie son jeton de session.
 *
 * Distinct de la reprise ordinaire, qui est rejouée à chaque chargement de
 * page et rouvrirait sans cesse un fil que l'équipe vient de clore.
 */
export async function reopenSupportConversation(
  publicId: string,
): Promise<SupportConversation> {
  const res = await api.post<SupportConversation>('/api/support/conversations/', {
    reopen: publicId,
  })
  if (res.data.session_token) {
    supportSession.set({ conv_id: res.data.id, token: res.data.session_token })
  }
  return res.data
}

export async function fetchSupportStatus(): Promise<{ online: boolean }> {
  const res = await api.get<{ online: boolean }>('/api/support/status/')
  return res.data
}

function briefBrowserUA(): string {
  if (typeof navigator === 'undefined') return ''
  const ua = navigator.userAgent
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|OPR)\/(\d+)/) || ua.match(/(MSIE|Trident)/)
  const browser = m ? `${m[1]} ${m[2] ?? ''}`.trim() : 'inconnu'
  const platform = /Mac/.test(ua)
    ? 'macOS'
    : /Win/.test(ua)
    ? 'Windows'
    : /Linux/.test(ua)
    ? 'Linux'
    : /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad/.test(ua)
    ? 'iOS'
    : 'inconnu'
  return `${browser} / ${platform}`
}

function buildContextPayload(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  return {
    page_url: window.location.href,
    browser: briefBrowserUA(),
    language: navigator.language || '',
  }
}

export async function createOrGetConversation(params: {
  guestEmail?: string
  guestName?: string
}): Promise<SupportConversation> {
  const existing = supportSession.get()
  const body: Record<string, string> = { ...buildContextPayload() }
  if (existing?.token) body.existing_token = existing.token
  if (params.guestEmail) body.guest_email = params.guestEmail
  if (params.guestName) body.guest_name = params.guestName

  const res = await api.post<SupportConversation>('/api/support/conversations/', body)
  if (res.data.session_token) {
    supportSession.set({ conv_id: res.data.id, token: res.data.session_token })
  }
  return res.data
}

export async function fetchMessages(convId: string, token: string): Promise<MessagesPayload> {
  const res = await api.get<MessagesPayload>(`/api/support/conversations/${convId}/messages/`, {
    params: { token },
  })
  return res.data
}

export async function sendMessage(
  convId: string,
  token: string,
  content: string,
): Promise<SupportMessage> {
  const res = await api.post<SupportMessage>(
    `/api/support/conversations/${convId}/messages/`,
    { content },
    { headers: { 'X-Support-Token': token } },
  )
  return res.data
}

export async function uploadAttachment(
  convId: string,
  token: string,
  file: File,
  content: string,
  onProgress?: (pct: number) => void,
): Promise<SupportMessage> {
  const form = new FormData()
  form.append('file', file)
  if (content) form.append('content', content)
  const res = await api.post<SupportMessage>(
    `/api/support/conversations/${convId}/upload/`,
    form,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Support-Token': token,
      },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
      },
    },
  )
  return res.data
}

export async function rateConversation(
  convId: string,
  token: string,
  rating: SupportRating,
  feedback?: string,
): Promise<void> {
  await api.post(
    `/api/support/conversations/${convId}/rating/`,
    { rating, feedback: feedback ?? '' },
    { headers: { 'X-Support-Token': token } },
  )
}

export function buildSupportStreamUrl(convId: string, token: string): string {
  const url = new URL('/api/support/stream/', API_BASE_URL)
  url.searchParams.set('conv', convId)
  url.searchParams.set('token', token)
  return url.toString()
}
